import { useEffect, useState } from "react";
import { getSite } from "@/lib/site";
import { currentLanguage } from "@/i18n";

/** The site content, in the reader's language.

    Cached in memory for the tab: every public page needs it, and re-fetching
    the same document on each navigation would put a spinner on a page that
    already has everything it needs to render. */
let cache = { lang: "", data: null };
// Several components ask for the site at once on first paint — the page, the
// footer, the script loader. Sharing the request in flight makes that one call.
let pending = { lang: "", promise: null };

export function useSite() {
  const lang = currentLanguage();
  const [site, setSite] = useState(cache.lang === lang ? cache.data : null);

  useEffect(() => {
    if (cache.lang === lang && cache.data) {
      setSite(cache.data);
      return undefined;
    }
    let alive = true;
    if (pending.lang !== lang || !pending.promise) {
      pending = { lang, promise: getSite(lang) };
    }
    pending.promise
      .then((data) => {
        cache = { lang, data };
        if (alive) setSite(data);
      })
      .catch(() => {
        pending = { lang: "", promise: null };
        // The vitrine must still render if the API is asleep — a free hosting
        // tier takes half a minute to wake, and a blank page is a worse answer
        // than the page with its default text.
        if (alive) setSite({});
      });
    return () => {
      alive = false;
    };
  }, [lang]);

  return site;
}

/** Title, description and link-preview tags for one page.

    Crawlers that do not run JavaScript never see these — the edge function in
    ``netlify/edge-functions`` is what serves them a rendered head. This is for
    the browser tab, and for the crawlers that do run scripts. */
export function useDocumentMeta({ title, description, image, url }) {
  useEffect(() => {
    if (title) document.title = title;

    const set = (selector, attribute, value) => {
      if (!value) return;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        const [key, name] = selector.replace(/meta\[|\]/g, "").split("=");
        tag.setAttribute(key, name.replace(/"/g, ""));
        document.head.appendChild(tag);
      }
      tag.setAttribute(attribute, value);
    };

    set('meta[name="description"]', "content", description);
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:description"]', "content", description);
    set('meta[property="og:image"]', "content", image);
    set('meta[property="og:url"]', "content", url);
    set('meta[name="twitter:title"]', "content", title);
    set('meta[name="twitter:description"]', "content", description);
    set('meta[name="twitter:image"]', "content", image);
  }, [title, description, image, url]);
}
